import React, { useRef, useState, useCallback, useMemo, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  View,
  Text,
  FlatList,
  RefreshControl,
  TouchableOpacity,
  ActivityIndicator,
  Animated,
  NativeSyntheticEvent,
  NativeScrollEvent,
  ViewToken,
} from 'react-native';
import { createStyles } from './DbInfiniteList.styles';
import type { DbInfiniteListProps, LoadingState, DbInfiniteListRef } from './DbInfiniteList.types';
import { useDbTheme } from '@/core';

// =====================================================
// 懒加载列表项包装组件（仅在开启 lazyLoad 时使用）
// =====================================================

interface LazyItemWrapperProps {
  /** 该 item 是否已进入可视区域 */
  isVisible: boolean;
  /** 占位高度 */
  placeholderHeight: number;
  /** 自定义占位组件 */
  placeholder?: React.ReactNode;
  /** 是否启用淡入动画 */
  fadeIn: boolean;
  /** 淡入动画时长 */
  fadeInDuration: number;
  /** 实际要渲染的内容 */
  children: React.ReactNode;
  /** 占位样式 */
  placeholderStyle: any;
  /** 占位闪烁样式 */
  shimmerStyle: any;
}

const LazyItemWrapper = React.memo<LazyItemWrapperProps>(({
  isVisible,
  placeholderHeight,
  placeholder,
  fadeIn,
  fadeInDuration,
  children,
  placeholderStyle,
  shimmerStyle,
}) => {
  // 记录是否曾经可见过（用于 fadeIn 判断：只在首次变可见时播放动画）
  const hasBeenVisibleRef = useRef(false);
  const fadeAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (isVisible && !hasBeenVisibleRef.current) {
      hasBeenVisibleRef.current = true;
      if (fadeIn) {
        // 首次进入可视区域，播放淡入动画
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: fadeInDuration,
          useNativeDriver: true,
        }).start();
      } else {
        fadeAnim.setValue(1);
      }
    }
  }, [isVisible, fadeIn, fadeInDuration, fadeAnim]);

  // 未进入可视区域 → 显示占位
  if (!isVisible) {
    if (placeholder) {
      return <View style={{ minHeight: placeholderHeight }}>{placeholder}</View>;
    }
    return (
      <View style={[placeholderStyle, { height: placeholderHeight }]}>
        <View style={shimmerStyle} />
      </View>
    );
  }

  // 已进入可视区域 → 显示真实内容
  if (fadeIn) {
    return (
      <Animated.View style={{ opacity: fadeAnim }}>
        {children}
      </Animated.View>
    );
  }

  return <>{children}</>;
});

// =====================================================
// 主组件
// =====================================================

function DbInfiniteListInner<T>({
  data,
  renderItem,
  keyExtractor,
  onRefresh,
  onLoadMore,
  refreshable = true,
  hasMore = true,
  loadingState: externalLoadingState,
  loadMoreThreshold = 0.3,
  loadMoreOffset,
  autoLoadOnMount = false,
  emptyComponent,
  loadingComponent,
  loadingMoreComponent,
  noMoreComponent,
  errorComponent,
  headerComponent,
  footerComponent,
  separatorComponent = true,
  style,
  contentContainerStyle,
  emptyText = '暂无数据',
  noMoreText = '没有更多了',
  loadingText = '加载中...',
  loadingMoreText = '加载更多...',
  errorText = '加载失败',
  retryText = '重试',
  onRetry,
  showScrollToTop = true,
  scrollToTopThreshold = 300,
  useSkeleton = false,
  skeletonCount = 5,
  skeletonComponent,
  backgroundColor,
  refreshTitle,
  refreshTitleStyle,
  refreshControlComponent,
  showsVerticalScrollIndicator = true,
  showLoadMoreOnEmpty = false,
  showNoMore = true,
  // ---- 懒加载配置（默认全部关闭，不影响已有逻辑）----
  lazyLoad = false,
  lazyItemHeight = 100,
  lazyPlaceholder,
  lazyViewabilityPercent = 1,
  lazyFadeIn = true,
  lazyFadeInDuration = 300,
  lazyRecycle = false,
  ...flatListProps
}: DbInfiniteListProps<T>, ref: React.Ref<DbInfiniteListRef>) {
  const theme = useDbTheme();
  const primaryColor = theme.colors.primary;
  const styles = useMemo(() => createStyles('light', backgroundColor), [backgroundColor]);
  const flatListRef = useRef<FlatList<T>>(null);
  const [internalLoadingState, setInternalLoadingState] = useState<LoadingState>('idle');
  const [showScrollTop, setShowScrollTop] = useState(false);
  const scrollTopAnim = useRef(new Animated.Value(0)).current;
  const isFirstLoadRef = useRef(true);
  const contentHeightRef = useRef(0);
  const layoutHeightRef = useRef(0);

  const loadingState = externalLoadingState ?? internalLoadingState;

  // =====================================================
  // 懒加载：可见性追踪
  // =====================================================

  // 记录当前可见的 item key 集合
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(new Set());
  // 记录曾经可见过的 item key 集合（用于非回收模式：一旦可见过就永远渲染真实内容）
  const everVisibleKeysRef = useRef<Set<string>>(new Set());

  // FlatList 的 viewabilityConfig（仅在 lazyLoad 开启时生效）
  const viewabilityConfig = useRef({
    itemVisiblePercentThreshold: lazyViewabilityPercent,
  }).current;

  // onViewableItemsChanged 回调（FlatList 要求必须用 useRef 包裹，不能在渲染期间改变）
  const onViewableItemsChanged = useRef(
    ({ viewableItems }: { viewableItems: ViewToken[] }) => {
      const newVisibleKeys = new Set<string>();
      viewableItems.forEach((token) => {
        if (token.isViewable && token.key) {
          newVisibleKeys.add(token.key);
          everVisibleKeysRef.current.add(token.key);
        }
      });
      setVisibleKeys(newVisibleKeys);
    }
  ).current;

  // 刷新时重置懒加载追踪状态
  useEffect(() => {
    if (lazyLoad && loadingState === 'refreshing') {
      everVisibleKeysRef.current.clear();
      setVisibleKeys(new Set());
    }
  }, [lazyLoad, loadingState]);

  // =====================================================
  // 原有逻辑（完全保持不变）
  // =====================================================

  // 显示/隐藏返回顶部按钮
  useEffect(() => {
    Animated.timing(scrollTopAnim, {
      toValue: showScrollTop ? 1 : 0,
      duration: 200,
      useNativeDriver: true,
    }).start();
  }, [showScrollTop, scrollTopAnim]);

  // 下拉刷新
  const handleRefresh = useCallback(async () => {
    if (!onRefresh || loadingState === 'refreshing') return;

    setInternalLoadingState('refreshing');
    try {
      await onRefresh();
    } finally {
      setInternalLoadingState('idle');
    }
  }, [onRefresh, loadingState]);

  // 加载更多
  const handleLoadMore = useCallback(async () => {
    if (!onLoadMore || !hasMore || loadingState !== 'idle') return;

    setInternalLoadingState('loadingMore');
    try {
      await onLoadMore();
    } finally {
      setInternalLoadingState('idle');
    }
  }, [onLoadMore, hasMore, loadingState]);

  // 检查是否需要加载更多（基于像素偏移）
  const checkLoadMoreByOffset = useCallback((offsetY: number) => {
    if (!loadMoreOffset || !contentHeightRef.current) return false;

    const distanceToBottom = contentHeightRef.current - offsetY - layoutHeightRef.current;
    return distanceToBottom <= loadMoreOffset;
  }, [loadMoreOffset]);

  // 滚动监听（合并外部传入的 onScroll）
  const handleScroll = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const offsetY = event.nativeEvent.contentOffset.y;
    const contentHeight = event.nativeEvent.contentSize.height;
    const layoutHeight = event.nativeEvent.layoutMeasurement.height;

    // 更新高度引用
    contentHeightRef.current = contentHeight;
    layoutHeightRef.current = layoutHeight;

    setShowScrollTop(offsetY > scrollToTopThreshold);

    // 如果使用 loadMoreOffset，在这里检查是否需要加载
    if (loadMoreOffset && checkLoadMoreByOffset(offsetY)) {
      // 避免首次加载时触发
      if (!isFirstLoadRef.current || autoLoadOnMount) {
        handleLoadMore();
      }
    }

    // 调用外部传入的 onScroll
    if (flatListProps.onScroll) {
      flatListProps.onScroll(event);
    }
  }, [scrollToTopThreshold, loadMoreOffset, autoLoadOnMount, checkLoadMoreByOffset, handleLoadMore, flatListProps]);

  // 处理内容布局变化
  const handleContentSizeChange = useCallback((width: number, height: number) => {
    contentHeightRef.current = height;
    // 标记首次加载完成
    if (isFirstLoadRef.current && data.length > 0) {
      isFirstLoadRef.current = false;
    }
  }, [data.length]);

  // 滚动到顶部
  const scrollToTop = useCallback((animated: boolean = true) => {
    flatListRef.current?.scrollToOffset({ offset: 0, animated });
  }, []);

  // 滚动到底部
  const scrollToEnd = useCallback((params?: { animated?: boolean }) => {
    flatListRef.current?.scrollToEnd(params);
  }, []);

  // 滚动到指定偏移量
  const scrollToOffset = useCallback((params: { offset: number; animated?: boolean }) => {
    flatListRef.current?.scrollToOffset(params);
  }, []);

  // 暴露 ref 方法
  useImperativeHandle(ref, () => ({
    scrollToOffset,
    scrollToEnd,
    scrollToTop,
    getFlatListRef: () => flatListRef.current,
  }), [scrollToOffset, scrollToEnd, scrollToTop]);

  // 重试
  const handleRetry = useCallback(() => {
    onRetry?.();
    handleLoadMore();
  }, [onRetry, handleLoadMore]);

  // =====================================================
  // 渲染列表项（核心改动点：lazyLoad 开关控制）
  // =====================================================

  const renderListItem = useCallback(({ item, index }: { item: T; index: number }) => {
    // ---- 未开启懒加载：走原有逻辑，直接渲染 ----
    // 注意：不能用 <></> (Fragment) 包裹，因为 FlatList 会给 cell 传 onLayout，Fragment 不接受该 prop
    if (!lazyLoad) {
      return renderItem(item, index) as React.ReactElement;
    }

    // ---- 开启懒加载：用 LazyItemWrapper 包裹 ----
    const key = keyExtractor(item, index);
    // 判断该 item 是否应该渲染真实内容
    const isCurrentlyVisible = visibleKeys.has(key);
    // 非回收模式下，曾经可见过的 item 永远保持渲染
    const shouldRender = lazyRecycle
      ? isCurrentlyVisible
      : (isCurrentlyVisible || everVisibleKeysRef.current.has(key));

    return (
      <LazyItemWrapper
        isVisible={shouldRender}
        placeholderHeight={lazyItemHeight}
        placeholder={lazyPlaceholder}
        fadeIn={lazyFadeIn}
        fadeInDuration={lazyFadeInDuration}
        placeholderStyle={styles.lazyPlaceholder}
        shimmerStyle={styles.lazyPlaceholderShimmer}
      >
        {renderItem(item, index)}
      </LazyItemWrapper>
    );
  }, [
    renderItem, lazyLoad, keyExtractor, visibleKeys, lazyRecycle,
    lazyItemHeight, lazyPlaceholder, lazyFadeIn, lazyFadeInDuration,
    styles.lazyPlaceholder, styles.lazyPlaceholderShimmer,
  ]);

  // 渲染分隔线
  const renderSeparator = useCallback(() => {
    // 如果传了自定义的分隔组件（React 元素），使用它
    // 注意：不能用 Fragment 包裹，FlatList 的 ItemSeparatorComponent 会传 onLayout 等 props
    if (separatorComponent && typeof separatorComponent === 'object') {
      return <View>{separatorComponent}</View>;
    }
    // 如果明确传了 false 或 null，不显示分隔线
    if (separatorComponent === false || separatorComponent === null) {
      return null;
    }
    // 默认显示分隔线
    return <View style={styles.separator} />;
  }, [separatorComponent, styles.separator]);

  // 渲染骨架屏
  const renderSkeleton = () => {
    if (skeletonComponent) {
      return (
        <>
          {Array.from({ length: skeletonCount }).map((_, index) => (
            <View key={`skeleton-${index}` as any}>{skeletonComponent}</View>
          ))}
        </>
      );
    }

    return (
      <>
        {Array.from({ length: skeletonCount }).map((_, index) => (
          <View key={`skeleton-${index}` as any} style={styles.skeletonItem}>
            <View style={styles.skeletonRow}>
              <View style={styles.skeletonAvatar} />
              <View style={styles.skeletonContent}>
                <View style={styles.skeletonTitle} />
                <View style={styles.skeletonDesc} />
              </View>
            </View>
          </View>
        ))}
      </>
    );
  };

  // 渲染空状态
  const renderEmpty = () => {
    // 首次加载中显示骨架屏或加载组件
    if (loadingState === 'loading') {
      if (useSkeleton) {
        return renderSkeleton();
      }
      if (loadingComponent) {
        return <View>{loadingComponent}</View>;
      }
      return (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={primaryColor} />
          <Text style={styles.loadingText}>{loadingText}</Text>
        </View>
      );
    }

    // 加载失败时返回 null，让 footer 的错误状态显示在底部
    // 避免同时显示"暂无数据"和"加载失败"
    if (loadingState === 'error') {
      return null;
    }

    // 空数据
    if (emptyComponent) {
      return <View>{emptyComponent}</View>;
    }

    return (
      <View style={styles.emptyContainer}>
        <Text style={styles.emptyText}>{emptyText}</Text>
      </View>
    );
  };

  // 渲染底部
  const renderFooter = () => {
    const footerContent = [];
    // 数据为空时，根据 showLoadMoreOnEmpty 决定是否显示加载更多相关内容
    const isEmpty = data.length === 0;

    // 自定义尾部
    if (footerComponent) {
      footerContent.push(
        <View key="custom-footer">{footerComponent}</View>
      );
    }

    // 加载更多状态 - 数据为空且不显示时跳过
    if (loadingState === 'loadingMore' && (!isEmpty || showLoadMoreOnEmpty)) {
      if (loadingMoreComponent) {
        footerContent.push(
          <View key="loading-more">{loadingMoreComponent}</View>
        );
      } else {
        footerContent.push(
          <View key="loading-more" style={styles.loadMoreContainer}>
            <ActivityIndicator size="small" color={primaryColor} />
            <Text style={styles.loadMoreText}>{loadingMoreText}</Text>
          </View>
        );
      }
    }

    // 没有更多 - 数据为空时不显示，showNoMore 为 false 时也不显示
    if (showNoMore && !hasMore && !isEmpty && loadingState === 'idle') {
      if (noMoreComponent) {
        footerContent.push(
          <View key="no-more">{noMoreComponent}</View>
        );
      } else {
        footerContent.push(
          <View key="no-more" style={styles.noMoreContainer}>
            <View style={styles.noMoreContent}>
              <View style={styles.noMoreLine} />
              <Text style={styles.noMoreText}>{noMoreText}</Text>
              <View style={styles.noMoreLine} />
            </View>
          </View>
        );
      }
    }

    // 错误状态
    if (loadingState === 'error') {
      if (errorComponent) {
        footerContent.push(
          <View key="error">{errorComponent}</View>
        );
      } else {
        footerContent.push(
          <View key="error" style={styles.errorContainer}>
            <Text style={styles.errorText}>{errorText}</Text>
            <TouchableOpacity style={styles.retryButton} onPress={handleRetry}>
              <Text style={styles.retryText}>{retryText}</Text>
            </TouchableOpacity>
          </View>
        );
      }
    }

    return footerContent.length > 0 ? <View>{footerContent}</View> : null;
  };

  // 渲染返回顶部按钮
  const renderScrollToTop = () => {
    if (!showScrollToTop) return null;

    return (
      <Animated.View
        style={[
          styles.scrollToTopButton,
          {
            opacity: scrollTopAnim,
            transform: [
              {
                scale: scrollTopAnim.interpolate({
                  inputRange: [0, 1],
                  outputRange: [0.8, 1],
                }),
              },
            ],
          },
        ]}
        pointerEvents={showScrollTop ? 'auto' : 'none'}
      >
        <TouchableOpacity onPress={() => scrollToTop()} activeOpacity={0.8}>
          <Text style={styles.scrollToTopIcon}>↑</Text>
        </TouchableOpacity>
      </Animated.View>
    );
  };

  return (
    <View style={[styles.container, style]}>
      <FlatList<T>
        ref={flatListRef}
        data={data}
        renderItem={renderListItem}
        keyExtractor={keyExtractor}
        ListHeaderComponent={headerComponent ? <View>{headerComponent}</View> : null}
        ListFooterComponent={renderFooter()}
        ListEmptyComponent={renderEmpty()}
        ItemSeparatorComponent={data.length > 0 ? renderSeparator : undefined}
        contentContainerStyle={[styles.contentContainer, contentContainerStyle]}
        onScroll={handleScroll}
        scrollEventThrottle={16}
        onEndReached={loadMoreOffset ? undefined : handleLoadMore}
        onEndReachedThreshold={loadMoreOffset ? undefined : loadMoreThreshold}
        onContentSizeChange={handleContentSizeChange}
        showsVerticalScrollIndicator={showsVerticalScrollIndicator}
        refreshControl={
          refreshable && onRefresh ? (
            refreshControlComponent ? (
              <View>{refreshControlComponent}</View>
            ) : (
              <RefreshControl
                refreshing={loadingState === 'refreshing'}
                onRefresh={handleRefresh}
                colors={[primaryColor]}
                tintColor={(refreshTitleStyle as any)?.color || primaryColor}
                title={refreshTitle}
              />
            )
          ) : undefined
        }
        // ---- 懒加载：仅在 lazyLoad=true 时注入 viewability 监听 ----
        {...(lazyLoad ? {
          onViewableItemsChanged,
          viewabilityConfig,
        } : {})}
        // 排除 onScroll，因为已经在上面 handleScroll 中处理了
        {...(({ onScroll: _onScroll, ...rest }) => rest)(flatListProps)}
      />
      {renderScrollToTop()}
    </View>
  );
}

/** 支持泛型的 forwardRef 组件类型 */
interface DbInfiniteListComponent {
  <T>(props: DbInfiniteListProps<T> & React.RefAttributes<DbInfiniteListRef>): React.ReactElement | null;
  displayName?: string;
}

const DbInfiniteList = forwardRef(DbInfiniteListInner) as unknown as DbInfiniteListComponent;

export default DbInfiniteList;
export type { DbInfiniteListProps, LoadingState, DbInfiniteListRef };
